import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import {
  Scale, UserCircle2, KeyRound, FileText, Ban, Coins, ShieldCheck,
  LogOut, AlertTriangle, RefreshCcw, MessageCircle, Sparkles,
} from "lucide-react";

export default function Terms() {
  const sections = [
    {
      id: "overview", title: "Overview", icon: FileText,
      body: (
        <>
          <p>
            These Terms of Service (the &quot;Terms&quot;) explain the rules that help keep MystiCast safe, fair, and enjoyable for everyone.
            They form a binding agreement between you and MystiCast (&quot;we&quot;, &quot;us&quot;, &quot;the Platform&quot;) and govern your access to and use of the MystiCast website, applications, APIs, and services (collectively, the &quot;Service&quot;).
          </p>
          <p>By creating an account, you accept these Terms.</p>
        </>
      ),
    },
    {
      id: "eligibility", title: "Eligibility", icon: UserCircle2,
      body: (
        <p>
          You must be at least <strong>18 years old</strong> to create an account or use the Service. By using MystiCast you confirm you meet this age requirement
          and that you have the legal capacity to enter into these Terms. If you are using MystiCast on behalf of an organization, you represent you have the authority to bind that organization.
        </p>
      ),
    },
    {
      id: "accounts-and-security", title: "Accounts & Security", icon: KeyRound, highlight: "cyan",
      body: (
        <>
          <InfoCard icon={ShieldCheck} title="🛡 Account Security">
            <p>You are responsible for all activity under your account. Use a strong password, enable 2FA where available, and never share your credentials.</p>
          </InfoCard>
          <ul>
            <li>You may not share your account or transfer it to another person.</li>
            <li>Notify us immediately if you suspect unauthorized access.</li>
            <li>We may suspend or terminate accounts that violate these Terms.</li>
          </ul>
        </>
      ),
    },
    {
      id: "content-you-post", title: "Content You Post", icon: FileText,
      body: (
        <>
          <p>
            You retain ownership of the video, audio, images, text, and other content you create and publish through the Service (&quot;Your Content&quot;).
            By posting Your Content you grant MystiCast a worldwide, non-exclusive, royalty-free license to host, store, reproduce, transmit, and display it solely to operate and improve the Service.
          </p>
          <p>
            You are solely responsible for Your Content. You represent that you own it or have all necessary rights, and that it does not violate any law or third-party right.
          </p>
        </>
      ),
    },
    {
      id: "prohibited-conduct", title: "Prohibited Conduct", icon: Ban, highlight: "pink",
      body: (
        <>
          <p>You agree not to:</p>
          <ul>
            <li>Publish sexual, sexually explicit, or pornographic content of any kind. MystiCast is not an adult platform.</li>
            <li>Publish content depicting the sexual exploitation of minors (CSAM). Reported immediately to authorities.</li>
            <li>Publish content that is illegal, incites violence, or promotes hate.</li>
            <li>Impersonate any person or misrepresent your affiliation with any entity.</li>
            <li>Circumvent, disable, or interfere with security or moderation features.</li>
            <li>Use the Service to run gambling operations or unlicensed money transmission.</li>
            <li>Scrape, harvest, or reverse-engineer the Service in violation of applicable law.</li>
            <li>Use the Service to distribute malware, phishing content, or spam.</li>
          </ul>
          <p>See the <Link to="/community">Community Guidelines</Link> and <Link to="/safety">Trust & Safety</Link> pages for full details.</p>
        </>
      ),
    },
    {
      id: "virtual-currencies", title: "Gold Coins & Sparks — Not Currency, Not Gambling", icon: Coins, highlight: "orange",
      body: (
        <>
          <p>
            MystiCast uses two <strong>closed-loop platform credits</strong> that only work inside MystiCast. Neither is a currency,
            security, cryptocurrency, or gambling instrument. Both are described in detail on the <Link to="/refunds">Refunds & Payouts page</Link>.
          </p>
          <InfoCard icon={Sparkles} title="Sparks — free engagement points">
            <p>
              Earned by watching streams and playing games. <strong>No monetary value.</strong> Cannot be purchased, transferred between users,
              withdrawn to cash, exchanged for currency, or redeemed outside the Platform. Not refundable.
            </p>
          </InfoCard>
          <InfoCard icon={Coins} title="♦ Gold Coins — non-refundable prepaid platform credit" tone="orange">
            <p>
              Purchased with fiat and used solely to send one-way tips to verified streamers or unlock cosmetic in-platform items.
              Gold Coins are <strong>non-refundable</strong>, <strong>non-transferable between users</strong>, <strong>non-withdrawable to cash</strong>,
              and have <strong>no monetary value outside the Platform</strong>. They cannot be sent, traded, or gifted from one viewer to another,
              and they cannot leave the Platform. Coin purchases are recorded as a deferred-revenue liability until the coins are spent.
              Analogous to Twitch Bits or YouTube Super Chat — a closed-loop prepaid credit.
            </p>
          </InfoCard>
          <InfoCard icon={Ban} title="🚫 No gambling, no games of chance" tone="pink">
            <p>
              All MystiCast games are <strong>free-to-play with no entry fees, no wagers, no cash prizes, and no convertible prizes</strong>.
              You cannot risk Gold Coins on a game outcome. Sparks earned from playing are non-withdrawable and cannot be exchanged for cash.
              We do not operate any lottery, raffle, sweepstakes, sportsbook, casino, or skill-gaming-for-prizes product.
            </p>
          </InfoCard>
        </>
      ),
    },
    {
      id: "verification", title: "Verification & KYC", icon: ShieldCheck,
      body: (
        <p>
          Streamers and tip recipients must complete identity verification through Stripe Identity. We collect only the minimum required data
          and do not store scans of your identification documents. See the <Link to="/privacy">Privacy Policy</Link> for details.
        </p>
      ),
    },
    {
      id: "termination", title: "Termination", icon: LogOut,
      body: (
        <p>
          We may suspend or terminate your access at any time for violation of these Terms. You may delete your account at any time via <Link to="/account">Account settings</Link>.
          Certain data may be retained after account deletion for legal, fraud-prevention, and safety purposes.
        </p>
      ),
    },
    {
      id: "disclaimers", title: "Disclaimers & Limitation of Liability", icon: AlertTriangle,
      body: (
        <>
          <p>
            The Service is provided &quot;as is&quot; and &quot;as available&quot;. To the maximum extent permitted by law, MystiCast disclaims all warranties, express or implied,
            including merchantability, fitness for a particular purpose, and non-infringement.
          </p>
          <p>
            In no event shall MystiCast be liable for indirect, incidental, special, consequential, or punitive damages arising out of your use of the Service.
            Total liability for any claim shall not exceed the greater of $100 or the amount you paid us in the past 12 months.
          </p>
        </>
      ),
    },
    {
      id: "changes", title: "Changes to These Terms", icon: RefreshCcw,
      body: (
        <p>We may update these Terms. Material changes will be announced via the Service or by email. Continued use after changes means acceptance.</p>
      ),
    },
    {
      id: "governing-law", title: "Governing Law", icon: Scale,
      body: (
        <p>These Terms are governed by the laws of the United States without regard to conflict-of-laws principles. Disputes will be resolved in the courts of the state where MystiCast is headquartered.</p>
      ),
    },
    {
      id: "contact", title: "Contact", icon: MessageCircle,
      body: (
        <p>Questions? Email <a href="mailto:legal@mysticast.tv">legal@mysticast.tv</a> for formal legal correspondence, or see the <Link to="/contact">Contact page</Link> for everything else.</p>
      ),
    },
  ];

  return (
    <LegalPage
      icon={Scale}
      label="TERMS OF SERVICE"
      title="Terms of Service"
      intro="These terms explain the rules that help keep MystiCast safe, fair, and enjoyable for everyone."
      updated="February 2026"
      sections={sections}
      contact={{
        title: "Have a question about the Terms?",
        body: "Email legal@mysticast.tv for formal legal correspondence, or use the contact form for general Terms questions.",
        cta: { label: "legal@mysticast.tv", to: "mailto:legal@mysticast.tv" },
      }}
    />
  );
}
